<template>
  <div class="d-inline">
    <b-modal
      :id="uid"
      :title="name"
      size="xl"
      scrollable
      centered
      @shown="onOpenModal"
      lazy
    >
      <div class="document-preview-modal position-relative">
        <LmComponentLoading v-if="isLoading" />
        <template v-else>
          <div v-if="!documentInPdf.src" class="d-flex justify-content-center align-items-center h-100">
            <LmSvgIcon
              wrapperClass="d-flex h-100 justify-content-center align-items-center flex-column p-4"
              width="6em"
              icon="LmNoDataIcon"
              text="No encontramos el PDF. Prueba iniciar el documento para crearlo."
            />
          </div>
          <div v-else>
            <LmFileViewer v-if="enableFileViewer" :files="fileList" />
            <iframe
              v-else
              :id="`${uid}-document-iframe`"
              :src="documentInPdf.src"
              width="100%"
              height="100%"
              frameborder="0"
              class="position-absolute"
            />
          </div>
        </template>
      </div>
      <template #modal-footer>
        <b-row>
          <LmButton
            class="col-auto mb-0"
            :value="$t('cancel')"
            buttonClass="btn-secondary"
            @click="$bvModal.hide(uid)"
          />
          <LmButton
            v-if="actionsAvailables.canBeRejectedByMe"
            class="col-auto mb-0"
            :value="$t('reject')"
            buttonClass="btn-danger"
            requireConfirmation
            confirmationTitle="¿Estas seguro de rechazar el documento?"
            confirmationMessage="El documento se pondrá en edición y se notificará al creador."
            @click="onReject"
          />
          <LmButton
            v-if="actionsAvailables.canBeSignedByMe"
            class="col-auto mb-0"
            :value="$t('sign')"
            buttonClass="btn-success"
            @click="onSign"
          />
          <LmButton
            v-if="actionsAvailables.canBeReviewedByMe"
            class="col-auto mb-0"
            :value="$t('review')"
            buttonClass="btn-success"
            @click="onReview"
          />
        </b-row>
      </template>
    </b-modal>
    <lmConfirmationModal
      v-if="documentLoaded"
      ref="lmConfirmationModalFirmarToken" title="Aviso"
      message="Va a iniciar el proceso de firma del documento.  ¿Está seguro?"
    />
    <LmSendOTP
      v-if="documentLoaded"
      ref="lmValidationModalFirmarToken" title="Aviso"
      message="Un código fue enviado a su mail, por favor ingreselo a continuación."
      @ok="otpToken = data.comment"
    />
  </div>
</template>

<script>
// Components
import LmFileViewer from '@/components/LmFileViewer'
import LmButton from '@/components/form/elements/LmButton'
import lmConfirmationModal from '@/components/modals/LmConfirmationModal'
import LmSendOTP from '@/components/modals/LmSendOTP'

// Utils
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'

export default {
  mixins: [lmToasts],
  props: {
    index: {
      type: Number,
      required: true
    },
    id: {
      type: String,
      required: true
    }
  },
  components: {
    LmButton,
    lmConfirmationModal,
    LmSendOTP,
    LmFileViewer
  },
  computed: {
    uid () {
      return this.id + this.index
    },
    enableLumileAppSigner () {
      return process.env?.VUE_APP_ENABLE_LUMILE_APP_SIGNER === 'yes'
    },
    enableFileViewer () {
      return process.env?.VUE_APP_ENABLE_FILE_VIEWER === 'yes'
    },
    fileList () {
      if (!this.documentInPdf.path) return null
      return [
        {
          ...this.documentInPdf,
          path: this.documentInPdf?.src,
          attachments: []
        }
      ]
    }
  },
  data () {
    return {
      lumbreSignerPort: process.env?.VUE_APP_LUMBRESIGNER_PORT || '8081',
      isLoading: false,
      documentLoaded: false,
      documentInPdf: {
        name: null,
        path: null,
        src: null
      },
      actionsAvailables: {
        canBeRejectedByMe: false,
        canBeReviewedByMe: false,
        canBeSignedByMe: false
      },
      name: null,
      documentTypeId: null,
      documentTypeSignature: null,
      otpToken: null
    }
  },
  methods: {
    async loadDocumentInPdf () {
      return commonsApi.axiosGetWrapper(`/documents/${this.id}`)
    },
    async onOpenModal () {
      try {
        this.isLoading = true
        const document = await this.loadDocumentInPdf()
        this.documentInPdf = document.data.documentInPdf || null
        this.name = document.data.documentName || null
        this.actionsAvailables.canBeRejectedByMe = document.data.canBeRejectedByMe || false
        this.actionsAvailables.canBeReviewedByMe = document.data.canBeReviewedByMe || false
        this.actionsAvailables.canBeSignedByMe = document.data.canBeSignedByMe || false
        this.documentTypeId = document.data.documentTypeId || null
        this.documentTypeSignature = document.data.documentTypeSignature || null

        this.documentLoaded = true
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },

    async onSign () {
      try {
        this.isLoading = true
        await this.$refs.lmConfirmationModalFirmarToken.lmShow()
        if (this?.documentTypeSignature === 'electronic') {
          await commonsApi.axiosPostWrapper('/documents/generateotp', {
            id: this.id
          })
          this.$refs.lmValidationModalFirmarToken.setIsCommentProps(true, true, 'Codigo', 'token')
          await this.$refs.lmValidationModalFirmarToken.lmShow()
          await commonsApi.axiosPostWrapper(`/documents/sign`, {
            id: this.id,
            token: this.otpToken
          })
          this.lmToastSuccessMessage(`Se firmó ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento firmado.')
        } else if (this?.documentTypeSignature === 'electronic_without_otp') {
          await commonsApi.axiosPostWrapper(`/documents/sign`, {
            id: this.id,
            token: this.otpToken
          })
          this.lmToastSuccessMessage(`Se firmó ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento firmado.')
        } else if (this?.documentTypeSignature === 'digital') {
          if (this.enableLumileAppSigner) {
            const r = await commonsApi.axiosGetWrapper(`/documents/generatejnlpV2/${this.id}`)
            await commonsApi.axiosPostWrapper(`http://localhost:${this.lumbreSignerPort}/initSR`, {
              url: r.data.targetURL
            })
          } else {
            const tempLink = document.createElement('a')
            tempLink.href = `${process.env.VUE_APP_API_URL}documents/generatejnlp/${this.id}`
            tempLink.setAttribute('download', `documento_afirmar.jnlp`)
            tempLink.click()
            this.lmToastSuccessMessage(`Para continuar debe finalizar el proceso de firma desde la aplicacion.`, 'Enviado al Firmador.')
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },

    async onReject () {
      try {
        this.isLoading = true
        await commonsApi.axiosPostWrapper(`/documents/reject`, {
          id: this.id
        })
        this.lmToastSuccessMessage(`Se rechazó el documento "${this.name || ' el documento'}" correctamente.`, 'Documento rechazado.')
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },

    async onReview () {
      try {
        await commonsApi.axiosPostWrapper(`/documents/review`, {
          id: this.id
        })
        this.lmToastSuccessMessage(`Se revisó el documento "${this.name || ' el documento'}" correctamente.`, 'Documento revisado.')
      } catch (error) {
        console.log(error)
      }
    }
  }
}
</script>

<style scoped>
.document-preview-modal {
  height: 70vh;
}
</style>
